/* THE SKIN BODY — mouvements discrets. Le contenu reste visible et utilisable sans ce fichier. */
(() => {
  'use strict';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const cleanups = new Map();

  /* Masque : flottement, profondeur au survol, défilement. Aucune fausse rotation 3D. */
  const initHero = hero => {
    if (cleanups.has(hero)) cleanups.get(hero)();
    const stage = hero.querySelector('[data-tsb-stage]');
    const tilt = hero.querySelector('[data-tsb-tilt]');
    const float = hero.querySelector('.tsb-stage__float');
    const shadow = hero.querySelector('.tsb-stage__shadow');
    if (!stage || !tilt || hero.dataset.motion !== 'true' || reduce.matches) return;
    const k = clamp(Number(hero.dataset.intensity) || 60, 0, 100) / 100;
    const maxRot = 7 * k, maxShift = 14 * k, scrollShift = 64 * k, scrollScale = 0.07 * k;
    const target = { x: 0, y: 0 }, cur = { x: 0, y: 0 };
    let scrollTarget = 0, scrollCur = 0, raf = 0, inView = true;
    stage.style.setProperty('--float-amp', (11 * k).toFixed(1) + 'px');
    float && float.classList.add('is-floating');
    shadow && shadow.classList.add('is-floating');
    const apply = () => {
      raf = 0;
      cur.x += (target.x - cur.x) * 0.08;
      cur.y += (target.y - cur.y) * 0.08;
      scrollCur += (scrollTarget - scrollCur) * 0.16;
      const s = stage.style;
      s.setProperty('--rx', (-cur.y * maxRot).toFixed(3) + 'deg');
      s.setProperty('--ry', (cur.x * maxRot).toFixed(3) + 'deg');
      s.setProperty('--mx', (cur.x * maxShift).toFixed(2) + 'px');
      s.setProperty('--my', (cur.y * maxShift * 0.6).toFixed(2) + 'px');
      s.setProperty('--sy', (-scrollCur * scrollShift).toFixed(2) + 'px');
      s.setProperty('--ss', (1 - scrollCur * scrollScale).toFixed(4));
      s.setProperty('--shadow-s', (1 - scrollCur * 0.25).toFixed(3));
      const moving = Math.abs(target.x - cur.x) > 0.001 || Math.abs(target.y - cur.y) > 0.001 || Math.abs(scrollTarget - scrollCur) > 0.0005;
      if (moving && inView) raf = requestAnimationFrame(apply);
    };
    const schedule = () => { if (!raf && inView) raf = requestAnimationFrame(apply); };
    const onMove = e => {
      if (!finePointer.matches) return;
      const r = stage.getBoundingClientRect();
      target.x = clamp((e.clientX - (r.left + r.width / 2)) / (r.width / 2), -1, 1);
      target.y = clamp((e.clientY - (r.top + r.height / 2)) / (r.height / 2), -1, 1);
      schedule();
    };
    const onLeave = () => { target.x = 0; target.y = 0; schedule(); };
    const onScroll = () => {
      const r = hero.getBoundingClientRect();
      scrollTarget = clamp(-r.top / Math.max(r.height, 1), 0, 1);
      schedule();
    };
    hero.addEventListener('pointermove', onMove, { passive: true });
    hero.addEventListener('pointerleave', onLeave, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    let io;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(entries => {
        inView = entries.some(en => en.isIntersecting);
        stage.classList.toggle('is-paused', !inView);
        if (inView) schedule();
      });
      io.observe(hero);
    }
    onScroll();
    cleanups.set(hero, () => {
      hero.removeEventListener('pointermove', onMove);
      hero.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('scroll', onScroll);
      io && io.disconnect();
      cancelAnimationFrame(raf);
      float && float.classList.remove('is-floating');
      shadow && shadow.classList.remove('is-floating');
      ['--rx', '--ry', '--mx', '--my', '--sy', '--ss', '--shadow-s', '--float-amp'].forEach(p => stage.style.removeProperty(p));
      cleanups.delete(hero);
    });
  };

  /* Appareil yeux : léger décalage au défilement. */
  const initParallax = section => {
    if (cleanups.has(section)) cleanups.get(section)();
    const img = section.querySelector('[data-tsb-parallax]');
    if (!img || section.dataset.motion !== 'true' || reduce.matches) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = section.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const p = clamp((vh - r.top) / (vh + r.height), 0, 1);
      img.style.setProperty('--ey', ((0.5 - p) * 28).toFixed(2) + 'px');
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    cleanups.set(section, () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); img.style.removeProperty('--ey'); cleanups.delete(section); });
  };

  /* Apparitions : les éléments déjà visibles ne sont jamais masqués. */
  let revealObserver;
  const initReveal = (scope = document) => {
    const items = [...scope.querySelectorAll('[data-tsb-reveal]:not(.is-in)')];
    if (!items.length) return;
    if (reduce.matches || !('IntersectionObserver' in window) || document.documentElement.dataset.tsbReveal === 'off') { items.forEach(el => el.classList.add('is-in')); return; }
    revealObserver = revealObserver || new IntersectionObserver(entries => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-in'); revealObserver.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
    const limit = window.innerHeight * 0.94;
    items.forEach(el => { if (el.getBoundingClientRect().top < limit) el.classList.add('is-in'); else revealObserver.observe(el); });
    document.documentElement.classList.add('tsb-reveal-on');
  };

  /* Comparateur avant / après (section désactivée tant qu'aucune preuve authentique n'est fournie). */
  const initCompare = (scope = document) => {
    scope.querySelectorAll('[data-tsb-compare]').forEach(box => {
      const range = box.querySelector('input[type=range]');
      if (!range || range.dataset.ready) return;
      range.dataset.ready = 'true';
      const set = () => box.style.setProperty('--pos', range.value + '%');
      range.addEventListener('input', set);
      set();
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-tsb-hero]').forEach(initHero);
    scope.querySelectorAll('[data-tsb-parallax-section]').forEach(initParallax);
    initReveal(scope);
    initCompare(scope);
  };

  const start = () => init();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  const onReduceChange = () => {
    if (reduce.matches) {
      [...cleanups.values()].forEach(fn => fn());
      document.querySelectorAll('[data-tsb-reveal]').forEach(el => el.classList.add('is-in'));
    } else init();
  };
  reduce.addEventListener ? reduce.addEventListener('change', onReduceChange) : reduce.addListener(onReduceChange);
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => {
    e.target.querySelectorAll('[data-tsb-hero],[data-tsb-parallax-section]').forEach(el => cleanups.has(el) && cleanups.get(el)());
  });
})();